(() => {
  'use strict';

  const q = (selector, context = document) => context.querySelector(selector);
  const qa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const debounce = (fn, wait = 180) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  };

  const dispatch = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));

  function openDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.body.classList.add('modal-open');
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('modal-open');
  }

  function bindDialogs(root = document) {
    qa('[data-dialog-open]', root).forEach((button) => {
      if (button.dataset.dialogBound === 'true') return;
      button.dataset.dialogBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        openDialog(document.getElementById(button.dataset.dialogOpen));
      });
    });

    qa('[data-dialog-close]', root).forEach((button) => {
      if (button.dataset.dialogBound === 'true') return;
      button.dataset.dialogBound = 'true';
      button.addEventListener('click', () => closeDialog(button.closest('dialog')));
    });

    qa('dialog', root).forEach((dialog) => {
      if (dialog.dataset.backdropBound === 'true') return;
      dialog.dataset.backdropBound = 'true';
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });
      dialog.addEventListener('close', () => document.body.classList.remove('modal-open'));
    });
  }

  function setLiveMessage(message) {
    const liveRegion = q('#ThemeLiveRegion');
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 40);
  }

  function updateCartCount(count) {
    qa('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = Number(count) === 0;
    });
  }

  async function getCart() {
    const response = await fetch(`${window.HobbyTheme.routes.cart}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  }

  async function refreshCartDrawer(openAfter = false) {
    const sectionId = 'cart-drawer';
    const existingDrawer = q('#CartDrawer');
    const keepOpen = Boolean(openAfter || (existingDrawer && existingDrawer.open));
    const separator = window.HobbyTheme.routes.cart.includes('?') ? '&' : '?';
    const [sectionResponse, cart] = await Promise.all([
      fetch(`${window.HobbyTheme.routes.cart}${separator}sections=${sectionId}`),
      getCart()
    ]);

    if (!sectionResponse.ok) throw new Error('Unable to refresh cart.');
    const sections = await sectionResponse.json();
    const html = sections[sectionId];
    if (html) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = q('#shopify-section-cart-drawer', documentFragment);
      const current = q('#shopify-section-cart-drawer');
      if (replacement && current) current.replaceWith(replacement);
    }

    updateCartCount(cart.item_count);
    bindDialogs();

    if (keepOpen) openDialog(q('#CartDrawer'));
    if (openAfter) setLiveMessage(window.HobbyTheme.strings.addedToCart);
    dispatch('cart:updated', { cart });
    return cart;
  }

  function setDrawerLoading(isLoading) {
    const drawer = q('#CartDrawer');
    if (drawer) drawer.classList.toggle('is-loading', isLoading);
  }

  async function addProductForm(form) {
    const submit = q('[type="submit"]', form);
    const error = q('[data-product-form-error]', form);
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    }
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      const response = await fetch(`${window.HobbyTheme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || window.HobbyTheme.strings.cartError);
      await refreshCartDrawer(true);
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
      setLiveMessage(err.message);
    } finally {
      if (submit) {
        submit.disabled = submit.dataset.variantAvailable === 'false';
        submit.removeAttribute('aria-busy');
      }
    }
  }

  function bindProductForms(root = document) {
    qa('[data-product-form]', root).forEach((form) => {
      if (form.dataset.ajaxBound === 'true') return;
      form.dataset.ajaxBound = 'true';
      if (form.dataset.ajaxCart !== 'true') return;
      form.addEventListener('submit', (event) => {
        if (!form.checkValidity()) return;
        event.preventDefault();
        addProductForm(form);
      });
    });
  }

  function bindQuantitySteppers(root = document) {
    qa('[data-quantity-wrapper]', root).forEach((wrapper) => {
      if (wrapper.dataset.quantityBound === 'true') return;
      wrapper.dataset.quantityBound = 'true';
      const input = q('input[type="number"]', wrapper);
      if (!input) return;
      wrapper.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-action]');
        if (!button) return;
        event.preventDefault();
        const current = Number(input.value || 1);
        const step = Number(input.step || 1);
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Infinity;
        const next = button.dataset.quantityAction === 'increase'
          ? Math.min(current + step, max)
          : Math.max(current - step, min);
        if (next === current) return;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  async function changeCartLine(line, quantity) {
    setDrawerLoading(true);
    try {
      const response = await fetch(`${window.HobbyTheme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || window.HobbyTheme.strings.cartError);
      await refreshCartDrawer(false);
      setLiveMessage(window.HobbyTheme.strings.cartUpdated);
    } catch (error) {
      setLiveMessage(error.message);
    } finally {
      setDrawerLoading(false);
    }
  }

  function bindCartActions() {
    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      changeCartLine(input.dataset.line, input.value);
    });

    document.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      changeCartLine(remove.dataset.line, 0);
    });
  }

  class ProductController {
    constructor(root) {
      this.root = root;
      this.formId = root.dataset.productFormId;
      this.form = document.getElementById(this.formId);
      this.variantSelect = q('[data-variant-id]', root);
      this.variantDataElement = q('[data-product-variants]', root);
      this.variants = this.variantDataElement ? JSON.parse(this.variantDataElement.textContent) : [];
      this.optionControls = qa('[data-option-position]', root);
      this.submit = this.form ? q('[name="add"]', this.form) : null;
      this.price = q('[data-product-price]', root);
      this.comparePrice = q('[data-product-compare-price]', root);
      this.sku = q('[data-product-sku]', root);
      this.inventory = q('[data-inventory-status]', root);
      this.bind();
      this.updateFromControls(false);
    }

    bind() {
      this.optionControls.forEach((control) => {
        control.addEventListener('change', () => this.updateFromControls(true));
      });
      qa('[data-media-thumbnail]', this.root).forEach((button) => {
        button.addEventListener('click', () => this.showMedia(button.dataset.mediaId));
      });
      const zoom = q('[data-media-zoom-open]', this.root);
      if (zoom) zoom.addEventListener('click', () => this.openZoom());
    }

    selectedOptions() {
      const selected = [];
      const grouped = new Map();
      this.optionControls.forEach((control) => {
        const position = Number(control.dataset.optionPosition);
        if (control.type === 'radio') {
          if (control.checked) grouped.set(position, control.value);
        } else {
          grouped.set(position, control.value);
        }
      });
      Array.from(grouped.keys()).sort((a, b) => a - b).forEach((key) => selected.push(grouped.get(key)));
      return selected;
    }

    updateFromControls(updateUrl) {
      if (!this.variants.length) return;
      const selected = this.selectedOptions();
      qa('[data-selected-option]', this.root).forEach((label) => {
        const position = Number(label.dataset.optionPosition || 0);
        if (position > 0 && selected[position - 1] !== undefined) label.textContent = selected[position - 1];
      });
      const variant = this.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]));
      this.setVariant(variant, updateUrl);
    }

    setVariant(variant, updateUrl) {
      if (!variant) {
        if (this.variantSelect) this.variantSelect.value = '';
        if (this.submit) {
          this.submit.dataset.variantAvailable = 'false';
          this.submit.disabled = true;
          const text = q('[data-add-to-cart-text]', this.submit);
          if (text) text.textContent = this.submit.dataset.unavailableText;
        }
        return;
      }

      if (this.variantSelect) this.variantSelect.value = variant.id;
      if (this.price) this.price.innerHTML = variant.formatted_price;
      if (this.comparePrice) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePrice.innerHTML = variant.formatted_compare_at_price;
          this.comparePrice.hidden = false;
        } else {
          this.comparePrice.hidden = true;
          this.comparePrice.textContent = '';
        }
      }
      if (this.sku) this.sku.textContent = variant.sku || this.sku.dataset.emptyText || '';
      this.updateInventory(variant);

      if (this.submit) {
        this.submit.dataset.variantAvailable = variant.available ? 'true' : 'false';
        this.submit.disabled = !variant.available;
        const text = q('[data-add-to-cart-text]', this.submit);
        if (text) text.textContent = variant.available ? this.submit.dataset.addText : this.submit.dataset.soldOutText;
      }

      if (variant.featured_media_id) this.showMedia(String(variant.featured_media_id));
      if (updateUrl && variant.url && window.history.replaceState) window.history.replaceState({}, '', variant.url);
      dispatch('variant:changed', { variant, sectionId: this.root.dataset.sectionId });
    }

    updateInventory(variant) {
      if (!this.inventory) return;
      const lowThreshold = Number(this.inventory.dataset.lowThreshold || 5);
      this.inventory.classList.remove('inventory-status--low', 'inventory-status--out');
      if (!variant.available) {
        this.inventory.textContent = this.inventory.dataset.outText;
        this.inventory.classList.add('inventory-status--out');
        return;
      }
      if (variant.inventory_management && variant.inventory_quantity <= lowThreshold && variant.inventory_quantity > 0) {
        this.inventory.textContent = this.inventory.dataset.lowText.replace('[quantity]', variant.inventory_quantity);
        this.inventory.classList.add('inventory-status--low');
        return;
      }
      this.inventory.textContent = this.inventory.dataset.inText;
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      qa('[data-media-item]', this.root).forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === String(mediaId)));
      qa('[data-media-thumbnail]', this.root).forEach((button) => button.setAttribute('aria-current', button.dataset.mediaId === String(mediaId) ? 'true' : 'false'));
    }

    openZoom() {
      const active = q('[data-media-item].is-active img', this.root);
      const dialog = q('[data-media-zoom-dialog]', this.root);
      const target = q('[data-media-zoom-content]', this.root);
      if (!active || !dialog || !target) return;
      target.innerHTML = '';
      const image = active.cloneNode(true);
      image.removeAttribute('loading');
      image.removeAttribute('sizes');
      target.appendChild(image);
      openDialog(dialog);
    }
  }

  function initProducts(root = document) {
    qa('[data-product-root]', root).forEach((element) => {
      if (element.dataset.productBound === 'true') return;
      element.dataset.productBound = 'true';
      try { new ProductController(element); } catch (error) { console.error(error); }
    });
  }

  function bindPredictiveSearch(root = document) {
    qa('[data-predictive-search-form]', root).forEach((form) => {
      if (form.dataset.predictiveBound === 'true') return;
      form.dataset.predictiveBound = 'true';
      const input = q('[data-predictive-search-input]', form);
      const results = q('[data-predictive-search-results]', form.parentElement || form);
      if (!input || !results) return;

      const search = debounce(async () => {
        const term = input.value.trim();
        if (term.length < 2) {
          results.innerHTML = '';
          results.hidden = true;
          return;
        }
        results.setAttribute('aria-busy', 'true');
        try {
          const url = `${window.HobbyTheme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=6`;
          const response = await fetch(url);
          if (!response.ok) throw new Error('Search failed');
          const text = await response.text();
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const section = q('#shopify-section-predictive-search', parsed);
          results.innerHTML = section ? section.innerHTML : '';
          results.hidden = !results.innerHTML.trim();
        } catch (error) {
          results.innerHTML = '';
          results.hidden = true;
        } finally {
          results.removeAttribute('aria-busy');
        }
      }, 220);

      input.addEventListener('input', search);
      input.addEventListener('focus', search);
      document.addEventListener('click', (event) => {
        if (!form.parentElement.contains(event.target)) results.hidden = true;
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          results.hidden = true;
          input.blur();
        }
      });
    });
  }

  function initRecommendations(root = document) {
    qa('[data-product-recommendations]', root).forEach((element) => {
      if (element.dataset.recommendationsBound === 'true') return;
      element.dataset.recommendationsBound = 'true';
      const url = element.dataset.url;
      if (!url) return;
      const load = async () => {
        try {
          const response = await fetch(url);
          const text = await response.text();
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const replacement = q('[data-product-recommendations]', parsed);
          if (replacement && replacement.innerHTML.trim()) element.innerHTML = replacement.innerHTML;
          bindProductForms(element);
          bindQuantitySteppers(element);
        } catch (error) {
          console.error(error);
        }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries, instance) => {
          if (!entries[0].isIntersecting) return;
          instance.disconnect();
          load();
        }, { rootMargin: '300px 0px' });
        observer.observe(element);
      } else load();
    });
  }

  function bindSortSelect(root = document) {
    qa('[data-sort-by]', root).forEach((select) => {
      if (select.dataset.sortBound === 'true') return;
      select.dataset.sortBound = 'true';
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });
  }

  function bindNewsletterValidation(root = document) {
    qa('[data-newsletter-form]', root).forEach((form) => {
      if (form.dataset.newsletterBound === 'true') return;
      form.dataset.newsletterBound = 'true';
      form.addEventListener('submit', () => {
        const email = q('input[type="email"]', form);
        if (email) email.value = email.value.trim();
      });
    });
  }

  function bindHeaderScroll() {
    const header = q('[data-site-header]');
    if (!header || header.dataset.scrollBound === 'true') return;
    header.dataset.scrollBound = 'true';
    const update = () => header.classList.toggle('has-scrolled', window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function bindShareButtons(root = document) {
    qa('[data-share-button]', root).forEach((button) => {
      if (button.dataset.shareBound === 'true') return;
      button.dataset.shareBound = 'true';
      button.addEventListener('click', async () => {
        const data = { title: document.title, url: window.location.href };
        if (navigator.share) {
          try { await navigator.share(data); } catch (_) { /* user cancelled */ }
        } else if (navigator.clipboard) {
          await navigator.clipboard.writeText(data.url);
          setLiveMessage(window.HobbyTheme.strings.linkCopied);
        }
      });
    });
  }

  function initialize(root = document) {
    bindDialogs(root);
    bindProductForms(root);
    bindQuantitySteppers(root);
    bindPredictiveSearch(root);
    initProducts(root);
    initRecommendations(root);
    bindSortSelect(root);
    bindNewsletterValidation(root);
    bindShareButtons(root);
    bindHeaderScroll();
  }

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');
  document.addEventListener('DOMContentLoaded', () => {
    initialize();
    bindCartActions();
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('details');
    if (details) details.open = true;
  });
})();
